<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t(id ? 'admin.basemaps.editBasemap' : 'admin.basemaps.addBasemap')"
    :loading="loading"
    :is-valid="isValid && (type === 'WMTS' ? !!connectedLayer : true)"
    :agree-text="id ? 'dialog.agreeSave' : 'dialog.agreeAdd'"
    dialog-width="700px"
    @agree="addLayer"
  >
    <template #body>
      <div class="tw:w-full">
        <v-form v-model="isValid">
          <div v-for="input in inputs" :key="input.name" class="tw:flexrow-2 tw:items-baseline">
            <div class="tw:w-1/3 tw:text-left">
              <div v-if="input.straightTitle">{{ input.straightTitle }}:</div>
              <div v-if="input.subtitle" class="tw:italic tw:text-xs tw:text-fontlight">{{ input.subtitle }}</div>
            </div>
            <div class="tw:grow">
              <template v-if="input.name === 'name'">
                <data-input :data-type="{ name: 'text' }" v-bind="input" v-model="name" />
              </template>
              <template v-if="input.name === 'type'">
                <v-radio-group
                  v-model="type"
                  :disabled="!!id"
                  @change="clearType"
                  row
                  hide-details
                  class="tw:mb-1.5 tw:mt-0"
                >
                  <v-radio value="WMTS" label="WMTS" />
                  <v-radio value="XYZ" label="XYZ" />
                </v-radio-group>
              </template>
              <template v-if="input.name === 'url'">
                <div class="tw:flexrow-2 tw:items-baseline">
                  <data-input
                    :data-type="{ name: 'text' }"
                    v-bind="input"
                    v-model="url"
                    :disabled="type === 'WMTS' && !!capabilities"
                    class="tw:grow"
                  />
                  <template v-if="type === 'WMTS'">
                    <base-button
                      v-if="!capabilities"
                      @click="fetchCapabilities"
                      :loading="isFetchCapabilitiesLoading"
                      class="mb-7"
                    >
                      {{ $i18n.t('menu.connect') }}
                    </base-button>
                    <base-button v-else @click="resetWmts" class="mb-7">
                      {{ $i18n.t('menu.reset') }}
                    </base-button>
                  </template>
                </div>
              </template>
              <template v-if="input.name === 'connectedLayer'">
                <data-input
                  :data-type="{ name: 'select' }"
                  v-bind="input"
                  v-model="connectedLayer"
                  :items="wmtsLayers"
                />
              </template>
              <template v-if="input.name === 'minZoom'">
                <data-input
                  :data-type="{ name: 'integer' }"
                  v-bind="input"
                  :min-value="0"
                  :max-value="28"
                  v-model="minZoom"
                />
              </template>
              <template v-if="input.name === 'maxZoom'">
                <data-input
                  :data-type="{ name: 'integer' }"
                  v-bind="input"
                  :min-value="0"
                  :max-value="28"
                  v-model="maxZoom"
                />
              </template>
              <template v-if="input.name === 'description'">
                <data-input :data-type="{ name: 'text' }" v-bind="input" v-model="description" />
              </template>
            </div>
          </div>
        </v-form>
      </div>
    </template>
  </main-large-dialog>
</template>

<script>
import MainLargeDialog from '@/components/MainLargeDialog';
import DataInput from '@/components/DataInput';

import { get, call } from 'vuex-pathify';
import { WMTSCapabilities } from 'ol/format';

import installCrs from '@/mixins/map/crs';
import { getWmtsOptions } from '@/assets/js/mapUtils';

export default {
  name: 'ManageBasemapDialog',
  mixins: [installCrs],
  components: {
    MainLargeDialog,
    DataInput,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number,
    },
  },
  data: () => ({
    isValid: false,
    loading: false,
    isFetchCapabilitiesLoading: false,

    type: 'XYZ',

    name: null,
    url: null,
    description: null,

    /** XYZ */
    minZoom: null,
    maxZoom: null,

    /** WMTS */
    connectedLayer: null,
    wmtsLayers: [],
    capabilities: null,
  }),
  computed: {
    basemapSchemaGroupId: get('layers/layersSchemaByScope@basemap@[0]@id'),
    manageBasemapMetadata: get('layers/basemapLayers@:id'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    inputs() {
      return [
        {
          name: 'name',
          straightTitle: this.$i18n.t('admin.basemaps.name'),
          rules: [v => !!v || 'rules.required'],
        },
        {
          name: 'type',
        },
        {
          name: 'url',
          straightTitle: this.$i18n.t('admin.basemaps.url'),
          rules: [v => !!v || 'rules.required'],
        },
        ...(this.type === 'XYZ'
          ? [
              {
                name: 'minZoom',
                straightTitle: this.$i18n.t('admin.basemaps.minZoom'),
                rules: [v => v >= 0 || 'rules.required'],
              },
              {
                name: 'maxZoom',
                straightTitle: this.$i18n.t('admin.basemaps.maxZoom'),
                rules: [v => v >= 0 || 'rules.required'],
              },
            ]
          : []),
        ...(this.type === 'WMTS' && this.capabilities
          ? [
              {
                name: 'connectedLayer',
                straightTitle: this.$i18n.t('admin.basemaps.serviceLayers'),
                rules: [v => !!v || 'rules.required'],
              },
            ]
          : []),
        {
          name: 'description',
          straightTitle: this.$i18n.t('admin.basemaps.attribution'),
          subtitle: this.$i18n.t('admin.basemaps.attributionSubtitle'),
        },
      ];
    },
  },
  methods: {
    addLayerAction: call('layers/addLayer'),
    editLayerAction: call('layers/editLayer'),
    getWmsCapabilities: call('layers/getWmsCapabilities'),
    clearType() {
      this.minZoom = this.maxZoom = this.connectedLayer = this.url = this.capabilities = null;
      this.wmtsLayers = [];
    },
    resetWmts() {
      this.wmtsLayers = [];
      this.connectedLayer = this.capabilities = null;
    },
    async fetchCapabilities() {
      try {
        this.resetWmts();
        this.isFetchCapabilitiesLoading = true;
        const r = await this.getWmsCapabilities({ url: this.url, service_type: 'wmts' });
        const parser = new WMTSCapabilities();
        this.capabilities = parser.read(r.data);
        this.wmtsLayers = (this.capabilities.Contents.Layer ?? []).map(layer => ({
          text: layer.Title,
          value: layer.Identifier,
        }));
        if (this.wmtsLayers.length === 1) {
          this.connectedLayer = this.wmtsLayers[0].value;
        }
        this.isFetchCapabilitiesLoading = false;
      } catch (error) {
        this.isFetchCapabilitiesLoading = false;
        console.log(error);
      }
    },
    async addLayer() {
      this.loading = true;
      try {
        let payload = {
          name: this.name,
          url: this.url,
          ...(this.description && { description: this.description }),
        };
        if (this.type === 'WMTS') {
          const parameters = getWmtsOptions(this.connectedLayer, this.capabilities);
          payload = {
            ...payload,
            service_type: 'wmts',
            service_layers_names: [this.connectedLayer],
            parameters,
          };
        } else {
          payload = {
            ...payload,
            service_type: 'xyz',
            service_layers_names: [this.name],
            parameters: {
              minZoom: this.minZoom,
              maxZoom: this.maxZoom,
            },
          };
        }
        if (this.id) {
          await this.editLayerAction({
            layer_type: 'service_layers',
            scope: 'basemap',
            layer_id: this.id,
            body: payload,
          });
        } else {
          await this.addLayerAction({
            layer_type: 'service-layers',
            body: {
              ...payload,
              layer_scope: 'basemap',
              layer_group: this.basemapSchemaGroupId,
            },
          });
        }
        this.computedIsVisible = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async init() {
      if (!this.manageBasemapMetadata) return;
      this.name = this.manageBasemapMetadata.name;
      this.url = this.manageBasemapMetadata.url;
      this.description = this.manageBasemapMetadata.description;
      if (this.manageBasemapMetadata.service_type === 'xyz') {
        this.type = 'XYZ';
        this.minZoom = this.manageBasemapMetadata.parameters?.minZoom;
        this.maxZoom = this.manageBasemapMetadata.parameters?.maxZoom;
      } else {
        this.type = 'WMTS';
        await this.fetchCapabilities();
        this.connectedLayer = this.manageBasemapMetadata.service_layers_names[0];
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>
